import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { PListe } from "./PrueflistePage";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../components/Toast";
import clsx from "clsx";
import { api } from "../lib/api";
import { SignedAmount } from "../components/SignedAmount";
import { erklaere, erklaerungFuer, sichtbareAnsicht } from "../../lib/steuer_ansicht.ts";
import type { Kontrolle18 } from "../../lib/kontrollvergleich18.ts";

/**
 * Steuer (Block 4): Rechnung je Steuerjahr aus bestätigten Buchungen + Jahreswerten (Einstellungen). Zuerst die Prüfung, ob die Rechnung
 * vollständig ist — fehlende Werte, offene Buchungen und Arten ohne Rolle werden genannt, nie still als 0 gerechnet.
 */
type Zeile = { key: string; label: string; art?: "euro" | "prozent" | "anzahl"; summe?: boolean; erklaerung?: string; erklaerungMehrere?: string };
type Antwort = {
  jahr: number; werte: Record<string, number>; hinweise: string[];
  fehltLabels: { schluessel: string; label: string }[];
  pruefung: { vollstaendig: boolean; unbestaetigt: number; geaendert: number; offenCent: number; konfliktCent: number; artenOhneRolle: { art: string; summeCent: number }[] };
  ansicht: { titel: string; zeilen: Zeile[] }[];
  abschluss: null | { version: number; am: number; von: string | null; grund: string | null; versionen: { version: number; am: number; von: string | null; grund: string | null }[];
    differenz: { geaendert: number; ergebnisAlt: number; ergebnisNeu: number; zeilen: { key: string; alt: number; neu: number }[] } };
};
const euro = (c: number) => (c / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export function SteuerPage() {
  const [sp, setSp] = useSearchParams();
  const { data: jahre } = useQuery<{ jahr: number }[]>({ queryKey: ["buchungen-jahre"], queryFn: () => api.get("/api/buchungen/jahre") });
  const jahr = Number(sp.get("jahr")) || jahre?.[0]?.jahr || null;
  const { data, isLoading } = useQuery<Antwort>({ queryKey: ["steuer", jahr], queryFn: () => api.get(`/api/steuer/${jahr}`), enabled: jahr !== null });
  const p = data?.pruefung;

  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-6">
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="text-[22px] font-semibold tracking-tight flex-1">Steuer</h1>
        <select className="border border-ios-line rounded-lg px-2 py-1.5 text-[16px] lg:text-[14px] bg-white" value={jahr ?? ""} aria-label="Steuerjahr"
          onChange={(e) => setSp({ jahr: e.target.value }, { replace: true })}>
          {/* das gewählte Jahr immer anbieten, auch wenn es noch keine Buchungen hat (sonst zeigt die Auswahl ein anderes Jahr) */}
          {[...new Set([...(jahre ?? []).map((j) => j.jahr), ...(jahr ? [jahr] : [])])].sort((x, y) => y - x).map((j) => <option key={j} value={j}>{j}</option>)}
        </select>
      </div>
      <p className="text-[13px] text-ios-muted mb-4">
        Berechnet aus bestätigten Buchungen und den Jahreswerten (Einstellungen › Jahreswerte). Indikation, kein Bescheid.
      </p>
      {isLoading || !data ? <p className="text-[14px] text-ios-muted">Lädt …</p> : (
        <div className="space-y-4">
          <section className={clsx("rounded-iosLg p-4 text-[14px]", p!.vollstaendig ? "bg-ios-green/10" : "bg-ios-blue/10")}>
            <div className={clsx("font-medium mb-1", p!.vollstaendig ? "text-ios-green" : "text-ios-blue")}>
              {p!.vollstaendig ? "Rechnung vollständig" : "Rechnung noch nicht vollständig"}
            </div>
            <ul className="list-disc ml-5 space-y-0.5">
              {p!.offenCent !== 0 && <li><Link className="text-ios-blue" to={`/buchungen?jahr=${data.jahr}&status=offen`}>Offener Rest ohne Buchungsart: {euro(p!.offenCent)}</Link></li>}
              {p!.unbestaetigt > 0 && <li><Link className="text-ios-blue" to={`/buchungen?jahr=${data.jahr}`}>{p!.unbestaetigt} Buchungen noch nicht bestätigt</Link></li>}
              {p!.geaendert > 0 && <li><Link className="text-ios-blue" to={`/buchungen?jahr=${data.jahr}&status=geaendert`}>{p!.geaendert} Buchungen seit dem Bestätigen geändert</Link></li>}
              {p!.konfliktCent !== 0 && <li><Link className="text-ios-blue" to={`/buchungen?jahr=${data.jahr}&status=konflikt`}>Konflikte: {euro(p!.konfliktCent)}</Link></li>}
              {p!.artenOhneRolle.map((a) => <li key={a.art}>Buchungsart „{a.art}“ ({euro(a.summeCent)}) hat keine Rolle in der Steuerrechnung</li>)}
              {data.fehltLabels.map((f) => <li key={f.schluessel}><Link className="text-ios-blue" to={`/einstellungen/jahreswerte?jahr=${data.jahr}`}>fehlt: {f.label}</Link></li>)}
              {data.hinweise.map((h) => <li key={h}>{h}</li>)}
            </ul>
          </section>
          <PrueflisteKurz jahr={data.jahr} />
          <Abschluss data={data} />
          <Paket jahr={data.jahr} />
          {sichtbareAnsicht(data.ansicht, data.werte).map((b) => (
            <section key={b.titel} className="bg-white rounded-iosLg shadow-card p-4">
              <h2 className="text-[15px] font-medium mb-2">{b.titel}</h2>
              <div className="divide-y divide-ios-line">
                {b.zeilen.map((z) => {
                  // Schlüssel mit „-“ = Vorzeichen für die Anzeige umkehren (Abzüge in der Steuerlast einheitlich als Betrag zeigen)
                  const neg = z.key.startsWith("-"), roh = data.werte[neg ? z.key.slice(1) : z.key];
                  const v = roh === undefined ? undefined : neg ? -roh : roh;
                  return (
                    <div key={z.key} className={clsx("flex justify-between gap-3 py-1.5 text-[14px]", z.summe && "font-medium")}>
                      <span className="text-ios-ink">{z.label}
                        {erklaerungFuer(z, data.werte) && <span className="block text-[12px] text-ios-muted font-normal">{erklaere(erklaerungFuer(z, data.werte)!, data.werte)}</span>}
                      </span>
                      {v === undefined ? <span className="text-ios-muted">—</span>
                        : z.art === "prozent" ? <span className="tabular-nums">{(v * 100).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %</span>
                        : z.art === "anzahl" ? <span className="tabular-nums">{v.toLocaleString("de-DE")}</span>
                        : <SignedAmount value={v} treatNegativeAsIncome={false} className="shrink-0" />}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
          <Kontrolle18Abschnitt jahr={data.jahr} />
        </div>
      )}
    </div>
  );
}

/**
 * Kontrollvergleich § 18 (nur nachrichtlich, Entscheidung 18.10.): Das Steuerergebnis nimmt § 18 aus Lexoffice. Hier stehen daneben die
 * § 18-Buchungen der App je Konto und ob sie einen Beleg oder ein Dokument haben. Einnahmen sind 1:1 vergleichbar, Ausgaben nicht
 * (Lexoffice führt zusätzlich Posten ohne Bankbuchung, z. B. Arbeitszimmer und AfA).
 */
function Kontrolle18Abschnitt({ jahr }: { jahr: number }) {
  const { data: k } = useQuery<Kontrolle18>({ queryKey: ["kontrolle18", jahr], queryFn: () => api.get(`/api/steuer/${jahr}/kontrolle18`) });
  if (!k) return null;
  const s = k.summe, lx = k.lexoffice;
  const nettoAusgaben = s.ausgabenCent + s.erstattungenCent;
  const diffEin = lx.einnahmen === null ? null : s.einnahmenCent - Math.round(lx.einnahmen * 100);
  const Betrag = ({ c }: { c: number }) => <SignedAmount value={c / 100} treatNegativeAsIncome={false} className="shrink-0" />;
  return (
    <section id="kontrolle18" className="bg-white rounded-iosLg shadow-card p-4 text-[14px] space-y-2">
      <h2 className="text-[15px] font-medium">Kontrollvergleich § 18 — Buchungen der App ↔ Lexoffice (nur nachrichtlich)</h2>
      <p className="text-[13px] text-ios-muted">
        Im Steuerergebnis zählt § 18 aus Lexoffice (Jahreswerte). Hier stehen die bestätigten Buchungen mit § 18-Anteil daneben — zum Abgleich, ohne Wirkung auf die Rechnung.
      </p>
      {k.rolleFehlt && <p className="text-ios-blue text-[13px]">Für {jahr} hat keine Buchungsart die Rolle „§ 18 kommt aus der EÜR (Lexoffice)“.</p>}
      {k.unbestaetigtMitAnteil > 0 && <p className="text-ios-blue text-[13px]">{k.unbestaetigtMitAnteil} noch nicht bestätigte Buchungen mit § 18-Anteil sind nicht enthalten.</p>}
      {k.konten.length === 0 ? <p className="text-ios-muted">Keine bestätigten § 18-Buchungen in {jahr}.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] tabular-nums">
            <thead><tr className="text-left text-ios-muted">
              <th className="font-normal py-1 pr-2">Konto</th><th className="font-normal pr-2 text-right">Ausgaben</th>
              <th className="font-normal pr-2 text-right">Erstattungen</th><th className="font-normal pr-2 text-right">Einnahmen</th>
              <th className="font-normal text-right">Belege</th>
            </tr></thead>
            <tbody className="divide-y divide-ios-line">
              {[...k.konten, { ...s, kontoId: "summe", konto: "Summe" }].map((z) => (
                <tr key={z.kontoId} className={clsx(z.kontoId === "summe" && "font-medium")}>
                  <td className="py-1 pr-2">{z.konto}</td>
                  <td className="pr-2 text-right"><Betrag c={z.ausgabenCent} /></td>
                  <td className="pr-2 text-right"><Betrag c={z.erstattungenCent} /></td>
                  <td className="pr-2 text-right"><Betrag c={z.einnahmenCent} /></td>
                  <td className={clsx("text-right", z.ohneNachweis ? "text-ios-blue" : "text-ios-green")}>
                    {z.ohneNachweis ? `${z.ohneNachweis} von ${z.buchungen} ohne` : `${z.buchungen} von ${z.buchungen} ✓`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="divide-y divide-ios-line">
        <div className="flex justify-between gap-3 py-1.5">
          <span>Einnahmen Lexoffice{lx.quelleEinnahmen && <span className="block text-[12px] text-ios-muted">{lx.quelleEinnahmen}</span>}</span>
          {lx.einnahmen === null ? <span className="text-ios-muted">— (Jahreswert fehlt)</span> : <SignedAmount value={lx.einnahmen} treatNegativeAsIncome={false} className="shrink-0" />}
        </div>
        {diffEin !== null && (
          <div className={clsx("flex justify-between gap-3 py-1.5", diffEin === 0 ? "text-ios-green" : "text-ios-blue")}>
            <span>{diffEin === 0 ? "✓ Einnahmen stimmen mit Lexoffice überein" : "○ Einnahmen App − Lexoffice"}</span>
            {diffEin !== 0 && <Betrag c={diffEin} />}
          </div>
        )}
        <div className="flex justify-between gap-3 py-1.5">
          <span>Ausgaben App (nach Erstattungen)</span><Betrag c={nettoAusgaben} />
        </div>
        <div className="flex justify-between gap-3 py-1.5">
          <span>Ausgaben Lexoffice{lx.quelleAusgaben && <span className="block text-[12px] text-ios-muted">{lx.quelleAusgaben}</span>}
            <span className="block text-[12px] text-ios-muted">nicht 1:1 vergleichbar: Lexoffice enthält zusätzlich Posten ohne Bankbuchung (z. B. Arbeitszimmer, AfA)</span>
          </span>
          {lx.ausgaben === null ? <span className="text-ios-muted">— (Jahreswert fehlt)</span> : <SignedAmount value={-lx.ausgaben} treatNegativeAsIncome={false} className="shrink-0" />}
        </div>
      </div>
      {k.ueberleitung && (
        <div>
          <h3 className="text-[14px] font-medium mt-2 mb-1">Überleitung Ausgaben (brutto)</h3>
          <div className="divide-y divide-ios-line">
            <div className="flex justify-between gap-3 py-1.5"><span>Ausgaben App (nach Erstattungen)</span><Betrag c={-k.ueberleitung.appAusgabenCent} /></div>
            <div className="flex justify-between gap-3 py-1.5"><span>+ Arbeitszimmer-Anteil, in Lexoffice gebucht<span className="block text-[12px] text-ios-muted">aus der Steuerrechnung (Strom, Gas, Wasser … × Flächenanteil Arbeitszimmer)</span></span><Betrag c={-k.ueberleitung.azLexofficeCent} /></div>
            <div className="flex justify-between gap-3 py-1.5 font-medium"><span>= App gesamt</span><Betrag c={-k.ueberleitung.appGesamtCent} /></div>
            <div className="flex justify-between gap-3 py-1.5"><span>Lexoffice Betriebsausgaben</span><Betrag c={-k.ueberleitung.lexofficeCent} /></div>
            <div className={clsx("flex justify-between gap-3 py-1.5", k.ueberleitung.restCent === 0 ? "text-ios-green" : "text-ios-blue")}>
              <span>{k.ueberleitung.restCent === 0 ? "✓ Ausgaben vollständig übergeleitet" : "○ Rest, noch ungeklärt (App − Lexoffice)"}</span>
              {k.ueberleitung.restCent !== 0 && <Betrag c={k.ueberleitung.restCent} />}
            </div>
          </div>
        </div>
      )}
      <p className="text-[12px] text-ios-muted">
        Erstattung = positive Buchung, der auf demselben Konto eine gleich hohe Ausgabe mit demselben Buchungstext gegenübersteht. Gemischte Verträge
        (z. B. Telefon) zählen mit ihrem § 18-Anteil laut Einstellungen › Verträge.
      </p>
    </section>
  );
}

/** Abgeschlossenes Jahr (User 28.10.2026, „A + B“): festgehaltenes Ergebnis, Differenz zum Stand nach Neu-Bestätigen, (neu) abschließen. */
function Abschluss({ data }: { data: Antwort }) {
  const qc = useQueryClient(), toast = useToast();
  const [grund, setGrund] = useState("");
  const a = data.abschluss;
  const label = new Map(data.ansicht.flatMap((b) => b.zeilen).map((z) => [z.key.replace(/^-/, ""), z.label]));
  const abschliessen = useMutation({
    mutationFn: () => api.post<{ version: number }>(`/api/steuer/${data.jahr}/abschliessen`, { grund }),
    onSuccess: (r) => { setGrund(""); toast.success(`Steuerjahr ${data.jahr} abgeschlossen (Version ${r.version})`); void qc.invalidateQueries({ queryKey: ["steuer", data.jahr] }); },
    onError: () => toast.error("Abschließen fehlgeschlagen"),
  });
  const d = a?.differenz;
  const datum = (s: number) => new Date(s * 1000).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  return (
    <section className={clsx("rounded-iosLg p-4 text-[14px] space-y-2", a ? "bg-ios-green/10" : "bg-white shadow-card")}>
      <h2 className="text-[15px] font-medium">{a ? `Steuerjahr ${data.jahr} abgeschlossen` : `Steuerjahr ${data.jahr} abschließen`}</h2>
      {a ? <>
        <p>Version {a.version} vom {datum(a.am)}{a.von ? ` (${a.von})` : ""}{a.grund ? ` — „${a.grund}“` : ""}. Die Zahlen unten sind dieser festgehaltene Stand.</p>
        {d && (d.zeilen.length === 0
          ? <p className="text-ios-green">Keine Abweichung: der aktuelle Stand ergibt dasselbe Ergebnis.</p>
          : <div className="rounded-ios bg-white p-3 space-y-1">
              <div className="font-medium text-ios-blue">Seit dem Abschluss geändert{d.geaendert ? ` (${d.geaendert} Buchungen neu zu bestätigen)` : ""}:
                Erstattung/Nachzahlung {euro(Math.round(d.ergebnisAlt * 100))} → {euro(Math.round(d.ergebnisNeu * 100))}</div>
              <ul className="text-[13px] space-y-0.5">
                {d.zeilen.filter((z) => label.has(z.key)).map((z) => <li key={z.key}>{label.get(z.key)}: {euro(Math.round(z.alt * 100))} → {euro(Math.round(z.neu * 100))}</li>)}
              </ul>
              <p className="text-[12px] text-ios-muted">Nur die Zahlen. Ob ein Bescheid noch geändert werden kann, entscheidet die Abgabenordnung — das prüft die App nicht.</p>
            </div>)}
        {a.versionen.length > 1 && <p className="text-[12px] text-ios-muted">Frühere Versionen: {a.versionen.slice(0, -1).map((v) => `${v.version} (${datum(v.am)}${v.grund ? `, ${v.grund}` : ""})`).join(" · ")}</p>}
      </> : <p className="text-ios-muted">Nach dem Erstellen des Steuerpakets: das Ergebnis festhalten. Danach fragt die App vor jeder Änderung, die dieses Jahr betrifft.</p>}
      <div className="flex flex-wrap gap-2 items-center">
        <input className="border border-ios-line rounded-lg px-2 py-1 text-[16px] lg:text-[13px] flex-1 min-w-[12rem]" placeholder={a ? "Grund der Korrektur" : "Notiz (z. B. „Paket an Steuerberater“)"}
          value={grund} onChange={(e) => setGrund(e.target.value)} />
        <button type="button" disabled={abschliessen.isPending || (!!a && !grund.trim())} onClick={() => abschliessen.mutate()}
          className="px-3 py-1.5 rounded-ios bg-ios-blue text-white disabled:opacity-40">{a ? "Neu abschließen" : "Jahr abschließen"}</button>
      </div>
    </section>
  );
}

/** Steuerberater-Paket: zeigt vor dem Erzeugen, ob die Rechnung vollständig ist und was fehlt; Erzeugen bleibt möglich. */
function Paket({ jahr }: { jahr: number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: v } = useQuery<{ vollstaendig: boolean; hinweise: string[]; dateien: string[]; belegStand: { buchungenOhneBeleg: number; belegeOhneBuchung: number; davonMitDokument: number } }>({
    queryKey: ["paket-vorschau", jahr], queryFn: () => api.get(`/api/steuer/${jahr}/paket/vorschau`) });
  const { data: einst } = useQuery<{ paketEmpfaenger: string | null }>({ queryKey: ["steuer-einstellungen"], queryFn: () => api.get("/api/steuer-einstellungen") });
  const { data: pl } = useQuery<PListe>({ queryKey: ["pruefliste", jahr], queryFn: () => api.get(`/api/steuer/${jahr}/pruefliste`) });
  const [name, setName] = useState("");
  const speichern = useMutation({
    mutationFn: () => api.put("/api/steuer-einstellungen", { paketEmpfaenger: name }),
    onSuccess: () => { toast.success("gespeichert"); void qc.invalidateQueries({ queryKey: ["steuer-einstellungen"] }); void qc.invalidateQueries({ queryKey: ["paket-vorschau"] }); },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  const belege = (v?.dateien ?? []).filter((d) => d.startsWith("03_Belege/")).length;
  return (
    <section className="bg-white rounded-iosLg shadow-card p-4 text-[14px] space-y-2">
      <h2 className="text-[15px] font-medium">Steuerberater-Paket {jahr}</h2>
      <p className="text-[13px] text-ios-muted">ZIP mit Lesehilfe und offenen Punkten, Steuerrechnung (PDF), Buchungsliste (CSV) und {belege} Belegen in Ordnern nach Gruppe.</p>
      {v && (v.belegStand.buchungenOhneBeleg > 0 || v.belegStand.belegeOhneBuchung > 0) && (
        <p className="text-ios-blue text-[13px]">
          {v.belegStand.buchungenOhneBeleg} Buchungen ohne geprüften Beleg{v.belegStand.davonMitDokument ? ` (davon ${v.belegStand.davonMitDokument} mit Dokument aus DEVONthink)` : ""} · {v.belegStand.belegeOhneBuchung} geprüfte Belege ohne Buchung{" "}
          <Link className="underline" to="/buchungen/abgleich">zum Abgleich</Link>
        </p>
      )}
      {v && !v.vollstaendig && <p className="text-ios-blue text-[13px]">Die Rechnung ist noch nicht vollständig — die offenen Punkte stehen vorne in der Lesehilfe.</p>}
      {(v?.hinweise ?? []).map((h) => <p key={h} className="text-ios-blue text-[13px]">{h}</p>)}
      {!einst?.paketEmpfaenger && (
        <div className="flex flex-wrap gap-2 items-center">
          <input className="border border-ios-line rounded-lg px-2 py-1.5 text-[16px] lg:text-[14px]" placeholder="Empfänger im Dateinamen (z. B. Nachname)" value={name} onChange={(e) => setName(e.target.value)} />
          <button type="button" disabled={!name.trim() || speichern.isPending} onClick={() => speichern.mutate()} className="px-3 py-1 rounded-ios bg-white shadow-card text-ios-blue disabled:opacity-40">speichern</button>
        </div>
      )}
      {/* Offene Punkte (ab 2025): erst zur Prüfliste — dort auflösen oder bewusst „trotzdem erstellen“ (User 27.10.2026) */}
      {pl?.sperrt
        ? <button type="button" onClick={() => navigate(`/pruefliste?jahr=${jahr}&vomPaket=1`)} className="inline-block px-3 py-1.5 rounded-ios bg-ios-blue text-white">Steuerpaket erstellen</button>
        : <a href={`/api/steuer/${jahr}/paket`} className="inline-block px-3 py-1.5 rounded-ios bg-ios-blue text-white">Steuerpaket erstellen</a>}
    </section>
  );
}

/** Kurzfassung der Prüfliste (die Liste selbst hat eine eigene Seite, seit 27.10.2026). */
function PrueflisteKurz({ jahr }: { jahr: number }) {
  const { data } = useQuery<PListe>({ queryKey: ["pruefliste", jahr], queryFn: () => api.get(`/api/steuer/${jahr}/pruefliste`) });
  if (!data) return null;
  return (
    <section className="bg-white rounded-iosLg shadow-card p-4 text-[14px] flex items-center gap-3">
      <span aria-hidden className={clsx("w-5 text-center", data.offen ? "text-ios-blue" : "text-ios-green")}>{data.offen ? "○" : "✓"}</span>
      <span className={clsx("flex-1", data.offen ? "text-ios-blue" : "text-ios-green")}>Prüfliste {jahr}: {data.offen ? `${data.offen} Punkte offen` : "alle Punkte erledigt"}</span>
      <Link to={`/pruefliste?jahr=${jahr}`} className="text-ios-blue">zur Prüfliste →</Link>
    </section>
  );
}
